import { Component, computed, effect, input, OnInit, signal } from '@angular/core';
import { Incident } from '../../models/incident';
import { AppModule } from "../../app.module";
import { DriverService } from '../../shared/driver.service';
import { TechDriverLocation } from '../../models/user';
import { NotificationService } from '../../shared/notification.service';
import { ServiceProviderClient } from '../../models/client';
import { ServiceProviderService } from '../../service-provider/service-provider.service';

@Component({
  selector: 'app-map-track-sp-tech',
  imports: [AppModule],
  templateUrl: './map-track-sp-tech.html',
  styleUrl: './map-track-sp-tech.less'
})
export class MapTrackSpTech {

  public incident = input<Incident | null>();
  public latestLocation = signal<TechDriverLocation>({} as TechDriverLocation);
  private animationFrame = signal(0);

  spTechPosition = computed(() => {
    const location = this.latestLocation();
    return {
      lat: location.locationLatitude || 0,
      lng: location.locationLongitude || 0
    };
  });

  private shouldFetchLocation = computed(() => {
    const incident = this.incident();
    return incident?.breakdownTechnician?.driverId;
  });

  constructor(private driverService: DriverService, private notification: NotificationService, private spService: ServiceProviderService) {

    effect(() => {
      const driverId = this.shouldFetchLocation();
      if (driverId) {
        this.fetchTechnicianLocation(driverId);
      }
    });

    const animate = () => {
      this.animationFrame.set(Date.now());
      requestAnimationFrame(animate);
    };
    animate();

  }

  incidentPosition = computed<google.maps.LatLngLiteral>(() => {
    if (this.incident() && this.incident()!.location){
      return {
        lat: this.incident()!.location.latitude,
        lng: this.incident()!.location.longitude
      };
    } else{
      return { lat: 0, lng: 0 };
    }
  });

  center = computed<google.maps.LatLngLiteral>(() => {
    const incidentPos = this.incidentPosition();
    const techPos = this.spTechPosition();
    return {
      lat: (incidentPos.lat + techPos.lat) / 2,
      lng: (incidentPos.lng + techPos.lng) / 2
    };
  });
  zoom = computed(() => {
    const incidentPos = this.incidentPosition();
    const techPos = this.spTechPosition();
    const latDiff = Math.abs(incidentPos.lat - techPos.lat);
    const lngDiff = Math.abs(incidentPos.lng - techPos.lng);
    const maxDiff = Math.max(latDiff, lngDiff);
    if (maxDiff < 0.01) return 15;
    if (maxDiff < 0.1) return 13;
    if (maxDiff < 0.5) return 11;
    if (maxDiff < 1) return 10;
    return 6;
  });


  ngOnInit() {
    const driverId = this.incident()?.breakdownTechnician?.driverId;
    if (driverId) {
      this.fetchTechnicianLocation(driverId);
    }
  }

  private fetchTechnicianLocation(driverId: number) {
    this.driverService.getTechDriverLatestLocation(driverId).subscribe({
      next: (location) => {
        this.latestLocation.set(location);
      },
      error: (err) => {
        this.notification.handleError('Error fetching technician location: ' + err.message);
      }
    });
  }

  public getIconMarker(): google.maps.Icon {
      return {
        url: '/icons/icon-72x72.png', 
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      };
    }

    public getDriverIconMarker(): google.maps.Icon {
      return {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="256" height="256" viewBox="0 0 256 256" xml:space="preserve">
            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform="translate(0 -2.842170943040401e-14) scale(2.81 2.81)">
              <circle cx="45" cy="45" r="45" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(255,255,255); fill-rule: nonzero; opacity: 1;" transform="  matrix(1 0 0 1 0 0) "/>
            </g>
            <g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;" transform="translate(46.17984832069338 46.17984832069335) scale(1.82 1.82)">
              <path d="M 25.371 54.774 c -3.914 0 -8.006 2.882 -9.634 6.676 H 3.556 c -0.552 0 -1 0.447 -1 1 s 0.448 1 1 1 h 11.592 c -0.27 1.66 0.029 3.227 0.891 4.432 c 1.036 1.447 2.733 2.244 4.779 2.244 c 4.145 0 8.493 -3.229 9.898 -7.353 c 0.729 -2.136 0.521 -4.232 -0.567 -5.754 C 29.114 55.571 27.417 54.774 25.371 54.774 z M 28.824 62.127 L 28.824 62.127 c -1.128 3.308 -4.719 5.999 -8.006 5.999 c -1.383 0 -2.502 -0.5 -3.152 -1.408 c -0.719 -1.005 -0.825 -2.405 -0.301 -3.944 c 1.128 -3.308 4.72 -5.999 8.006 -5.999 c 1.383 0 2.502 0.5 3.152 1.408 C 29.242 59.188 29.349 60.589 28.824 62.127 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round"/>
              <path d="M 89.845 46.609 l -3.363 -17.102 c -0.303 -1.54 -1.592 -2.536 -3.284 -2.536 H 64.329 l 1.049 -2.755 c 0.375 -0.984 0.242 -2.09 -0.355 -2.958 s -1.584 -1.386 -2.638 -1.386 H 24.85 c -0.552 0 -1 0.448 -1 1 s 0.448 1 1 1 h 37.535 c 0.401 0 0.763 0.19 0.991 0.521 c 0.228 0.331 0.276 0.735 0.133 1.111 l -8.574 22.522 c -0.333 0.874 -1.186 1.462 -2.121 1.462 H 1 c -0.552 0 -1 0.447 -1 1 s 0.448 1 1 1 h 51.813 c 1.76 0 3.363 -1.104 3.99 -2.75 l 6.764 -17.767 h 19.63 c 0.343 0 1.158 0.09 1.321 0.921 l 0.468 2.378 h -6.957 c -1.807 0 -3.699 1.399 -4.309 3.185 l -2.361 6.925 c -0.339 0.995 -0.231 1.989 0.297 2.726 c 0.502 0.701 1.341 1.104 2.302 1.104 h 13.77 l 0.154 0.784 c 0.24 1.218 0.111 2.61 -0.37 4.025 l -2.673 7.838 c -0.479 1.404 -2.053 2.591 -3.436 2.591 h -2.476 c 0.27 -1.66 -0.029 -3.226 -0.891 -4.431 c -1.036 -1.448 -2.733 -2.245 -4.779 -2.245 c -3.914 0 -8.008 2.882 -9.634 6.676 H 39.77 c -0.552 0 -1 0.447 -1 1 s 0.448 1 1 1 h 23.264 c -0.27 1.66 0.029 3.227 0.891 4.431 c 1.036 1.448 2.733 2.245 4.779 2.245 c 3.915 0 8.008 -2.882 9.635 -6.676 h 3.065 c 2.232 0 4.573 -1.732 5.328 -3.944 l 2.673 -7.839 C 89.999 49.92 90.151 48.172 89.845 46.609 z M 68.704 68.126 c -1.383 0 -2.503 -0.5 -3.152 -1.408 c -0.719 -1.005 -0.825 -2.405 -0.301 -3.945 c 1.128 -3.308 4.719 -5.998 8.006 -5.998 c 1.383 0 2.503 0.5 3.152 1.409 c 0.719 1.004 0.825 2.405 0.301 3.943 v 0.001 C 75.582 65.436 71.991 68.126 68.704 68.126 z M 87.334 44.211 H 73.958 c -0.221 0 -0.518 -0.046 -0.676 -0.268 c -0.147 -0.206 -0.158 -0.541 -0.03 -0.917 l 2.361 -6.924 c 0.333 -0.975 1.462 -1.831 2.416 -1.831 h 7.35 l 1.087 5.526 L 87.334 44.211 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round"/>
              <path d="M 9.227 35.242 h 40.719 c 0.553 0 1 -0.448 1 -1 s -0.447 -1 -1 -1 H 9.227 c -0.552 0 -1 0.448 -1 1 S 8.675 35.242 9.227 35.242 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round"/>
            </g>
            </svg>
          `)}`,
          scaledSize: new google.maps.Size(28, 28),
          anchor: new google.maps.Point(14, 14)
        };
      }

  public getPulseIcon(): google.maps.Symbol {
    // Make this reactive by referencing the animation signal
    const time = this.animationFrame() / 250;
    
    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 20 + Math.sin(time) * 8,
      fillColor: '#00bd48',  
      fillOpacity: 0.2 + Math.sin(time) * 0.1, 
      strokeColor: '#00bd48',
      strokeWeight: 2,
      strokeOpacity: 0.4 + Math.sin(time) * 0.2
    };
  }

  public get MinsAgo(): number {
    const dtLogged = this.latestLocation().dtLogged;
    if (!dtLogged) return 0;
    const loggedTime = new Date(dtLogged + 'Z').getTime() ;
    const currentTime = new Date().getTime();
    const diffMins = Math.floor((currentTime - loggedTime) / 60000);
    return diffMins;
  }

   public requestStatusUpdate(sp: ServiceProviderClient) {
    //TODO: Ask if there is an endpoint to ask a specific SP tech to update their status
      return this.spService.requestTechnicianStatusUpdate(sp.clientId).subscribe({
        next: () => {
          this.notification.showSuccess('Status update requested successfully.');
        },
        error: (error) => {
          this.notification.handleError('Error requesting status update: ', error.message);
        }
      });
  }
}
